import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Alert } from 'react-bootstrap';
import swal from "sweetalert";
import { getSavedJobs, unsaveJob } from '../../../services/SavedJobsService';
import { LogoSpinner } from '../../../components/LogoSpinner';

const formatSalary = (min, max) => {
	if (!min && !max) return 'No especificado';
	if (min && max) {
		return `RD$ ${min.toLocaleString('es-DO')} - RD$ ${max.toLocaleString('es-DO')}`;
	}
	if (min) return `Desde RD$ ${min.toLocaleString('es-DO')}`;
	if (max) return `Hasta RD$ ${max.toLocaleString('es-DO')}`;
	return 'No especificado';
};

const formatDate = (dateString) => {
	if (!dateString) return 'N/A';
	const date = new Date(dateString);
	return date.toLocaleDateString('es-ES', {
		year: 'numeric',
		month: 'long',
		day: 'numeric'
	});
};

export const SavedJobs = () => {
	const [savedJobs, setSavedJobs] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [pagination, setPagination] = useState({
		page: 1,
		limit: 10,
		total: 0,
		totalPages: 0
	});
	const [removingId, setRemovingId] = useState(null);

	useEffect(() => {
		loadSavedJobs();
	}, [pagination.page]);

	const loadSavedJobs = async () => {
		try {
			setLoading(true);
			setError(null);
			const response = await getSavedJobs({
				page: pagination.page,
				limit: pagination.limit
			});

			setSavedJobs(response.data.data || []);
			setPagination({
				...pagination,
				total: response.data.total || 0,
				totalPages: response.data.totalPages || 0
			});
		} catch (err) {
			console.error('Error loading saved jobs:', err);
			setError(err.response?.data?.message || 'Error al cargar las vacantes guardadas');
		} finally {
			setLoading(false);
		}
	};

	const handleRemove = async (vacanteId, titulo) => {
		const willDelete = await swal({
			title: "¿Estás seguro?",
			text: `¿Deseas eliminar "${titulo}" de tus vacantes guardadas?`,
			icon: "warning",
			buttons: ["Cancelar", "Eliminar"],
			dangerMode: true,
		});

		if (willDelete) {
			try {
				setRemovingId(vacanteId);
				await unsaveJob(vacanteId);
				swal("¡Eliminado!", "La vacante ha sido eliminada de tus guardados.", "success");
				loadSavedJobs();
			} catch (err) {
				console.error('Error removing saved job:', err);
				swal("Error", err.response?.data?.message || "No se pudo eliminar la vacante", "error");
			} finally {
				setRemovingId(null);
			}
		}
	};

	const handlePageChange = (newPage) => {
		if (newPage >= 1 && newPage <= pagination.totalPages) {
			setPagination({ ...pagination, page: newPage });
			window.scrollTo(0, 0);
		}
	};

	if (loading) {
		return <LogoSpinner />;
	}

	return (
		<div className="job-bx save-job browse-job table-job-bx clearfix">
			<div className="job-bx-title clearfix">
				<h5 className="font-weight-700 pull-left text-uppercase">
					{savedJobs.length} Vacante{savedJobs.length !== 1 ? 's' : ''} guardada{savedJobs.length !== 1 ? 's' : ''}
				</h5>
			</div>

			{error && (
				<Alert variant="danger" onClose={() => setError(null)} dismissible>
					{error}
				</Alert>
			)}

			{savedJobs.length === 0 ? (
				<div className="text-center p-5">
					<i className="fa fa-heart-o" style={{ fontSize: '64px', color: '#ccc', marginBottom: '20px' }}></i>
					<h4>No tienes vacantes guardadas</h4>
					<p className="text-muted">Explora las vacantes disponibles y guarda las que te interesen</p>
					<Link to="/browse-job-filter-grid" className="site-button m-t20">
						Buscar Vacantes
					</Link>
				</div>
			) : (
				<>
					<table>
						<thead>
							<tr>
								<th>Título</th>
								<th>Ubicación</th>
								<th>Salario</th>
								<th>Modalidad</th>
								<th>Guardada</th>
								<th>Acciones</th>
							</tr>
						</thead>
						<tbody>
							{savedJobs.map((vacancy) => {
								const location = `${vacancy.ciudad?.nombre || 'N/A'}, ${vacancy.pais?.nombre || 'N/A'}`;
								const salary = formatSalary(vacancy.salarioMinimo, vacancy.salarioMaximo);
								const modalidad = vacancy.modalidadTrabajo?.nombre || 'N/A';

								return (
									<tr key={vacancy.id}>
										<td className="job-name">
											<Link to={`/job-detail?id=${vacancy.id}`} style={{ color: '#009247', fontWeight: '600' }}>
												{vacancy.titulo}
											</Link>
											{vacancy.tags && vacancy.tags.length > 0 && (
												<div className="m-t5">
													{vacancy.tags.slice(0, 3).map((tagItem, idx) => (
														<span
															key={idx}
															className="badge badge-secondary m-r5"
															style={{
																background: '#f5f5f5',
																color: '#666',
																padding: '3px 8px',
																borderRadius: '3px',
																fontSize: '11px'
															}}
														>
															{tagItem.tag?.nombre}
														</span>
													))}
												</div>
											)}
										</td>
										<td className="criterias">
											<i className="ti-location-pin m-r5"></i>
											{location}
										</td>
										<td className="criterias">
											<i className="ti-money m-r5"></i>
											{salary}
										</td>
										<td className="criterias">
											<i className="ti-bookmark m-r5"></i>
											{modalidad}
										</td>
										<td className="date">{formatDate(vacancy.createdAt)}</td>
										<td className="job-links">
											<Link
												to={`/job-detail?id=${vacancy.id}`}
												className="btn btn-primary btn-sm m-r5"
												title="Ver detalles"
											>
												<i className="fa fa-eye"></i>
											</Link>
											<button
												className="btn btn-danger btn-sm"
												onClick={() => handleRemove(vacancy.id, vacancy.titulo)}
												disabled={removingId === vacancy.id}
												title="Eliminar de guardados"
											>
												{removingId === vacancy.id ? (
													<i className="fa fa-spinner fa-spin"></i>
												) : (
													<i className="ti-trash"></i>
												)}
											</button>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>

					{pagination.totalPages > 1 && (
						<div className="pagination-bx float-right m-t30">
							<ul className="pagination">
								<li className={`previous ${pagination.page === 1 ? 'disabled' : ''}`}>
									<Link
										to="#"
										onClick={(e) => {
											e.preventDefault();
											if (pagination.page > 1) handlePageChange(pagination.page - 1);
										}}
									>
										<i className="ti-arrow-left"></i> Anterior
									</Link>
								</li>
								{[...Array(pagination.totalPages)].map((_, index) => {
									const pageNum = index + 1;
									return (
										<li key={pageNum} className={pagination.page === pageNum ? 'active' : ''}>
											<Link
												to="#"
												onClick={(e) => {
													e.preventDefault();
													handlePageChange(pageNum);
												}}
											>
												{pageNum}
											</Link>
										</li>
									);
								})}
								<li className={`next ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
									<Link
										to="#"
										onClick={(e) => {
											e.preventDefault();
											if (pagination.page < pagination.totalPages) handlePageChange(pagination.page + 1);
										}}
									>
										Siguiente <i className="ti-arrow-right"></i>
									</Link>
								</li>
							</ul>
						</div>
					)}
				</>
			)}
		</div>
	);
};

